import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  IndianRupee,
  LogOut,
  Store,
  ChevronDown,
  User,
  Settings,
  ToggleLeft,
  ToggleRight,
  Clock,
  Ban,
  AlertTriangle,
  FileText,
  Layers,
  Monitor,
  Warehouse,
} from "lucide-react";
import PanelLayout from "@/components/shared/panel/PanelLayout";
import { ChannelDeskProvider, retailerDesk } from "@/lib/channel-desk";
import type { PanelNavEntry } from "@/components/shared/panel/PanelSidebar";
import NotificationBell from "@/components/shared/notifications/NotificationBell";
import { useLogout } from "@/hooks/useLogout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useMyStore, useStoreDashboard, useStoreId } from "@/hooks/queries";
import { StoreStatus } from "@/types/enums";

function getInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "ST";
}

function buildStoreNav(pendingOrders: number): PanelNavEntry[] {
  return [
    { title: "Overview", url: "/store", icon: LayoutDashboard },
    { title: "Products", url: "/store/products", icon: Package },
    {
      title: "Orders",
      url: "/store/orders",
      icon: ShoppingCart,
      badge: pendingOrders > 0 ? pendingOrders : undefined,
    },
    { title: "POS Billing", url: "/store/pos", icon: Monitor },
    { title: "Earnings", url: "/store/earnings", icon: IndianRupee },
    { title: "Receive BV", url: "/store/bv", icon: Layers },
    {
      type: "group",
      title: "From wholesaler",
      icon: Warehouse,
      children: [
        { title: "Catalogue", url: "/store/supply" },
        { title: "Channel stock", url: "/store/supply/stock" },
        { title: "Supply orders", url: "/store/supply/orders" },
        { title: "Retail commission", url: "/store/supply/commission" },
      ],
    },
    { title: "Reports", url: "/store/reports", icon: FileText },
  ];
}

const pageTitles: Record<string, string> = {
  "/store": "Dashboard",
  "/store/products": "Products",
  "/store/products/new": "Add Product",
  "/store/orders": "Orders",
  "/store/pos": "POS Billing",
  "/store/earnings": "Earnings",
  "/store/bv": "Receive BV",
  "/store/supply": "Wholesaler catalogue",
  "/store/supply/stock": "Channel stock",
  "/store/supply/orders": "Supply orders",
  "/store/supply/commission": "Retail commission",
  "/store/reports/sales": "Sales Report",
  "/store/reports/payments": "Payment Report",
  "/store/reports/bv": "BV Report",
  "/store/reports/products": "Product Report",
  "/store/kyc": "Store KYC",
  "/store/profile": "Store Profile",
  "/store/notifications": "Notifications",
};

const StoreLayout = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { storeId } = useStoreId();
  const { data: store, isLoading: isLoadingStore } = useMyStore(Boolean(storeId));
  const { data: dashboard } = useStoreDashboard(storeId);

  const storeName = store?.storeName ?? "Store Panel";
  const ownerLabel = user?.name ?? "Store Owner";
  const ownerEmail = store?.email ?? user?.email ?? "";
  const isOpen = store?.isOpen ?? false;
  const pendingOrders = dashboard?.pendingOrders ?? 0;
  const initials = getInitials(ownerLabel);
  const logout = useLogout();

  const isKycPage = location.pathname === "/store/kyc";

  if (store && store.status !== StoreStatus.Active && !isKycPage) {
    const isPending = store.status === StoreStatus.Pending;
    const isSuspended = store.status === StoreStatus.Suspended;
    const isRejected = store.status === StoreStatus.Rejected;

    return (
      <div className="min-h-screen bg-background dark:bg-black flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-6">
          <div className="flex justify-center items-center gap-2">
            <Store className="h-8 w-8 text-primary" />
            <span className="text-xl font-bold tracking-tight font-heading">Bihari Mall Merchant</span>
          </div>

          <div className="rounded-2xl border border-border bg-card shadow-lg overflow-hidden">
            <div className={`p-6 flex justify-center border-b ${
              isPending ? "bg-warning/5 border-warning/10" : "bg-destructive/5 border-destructive/10"
            }`}>
              {isPending && <Clock className="h-16 w-16 text-warning animate-pulse" />}
              {isSuspended && <Ban className="h-16 w-16 text-destructive" />}
              {isRejected && <AlertTriangle className="h-16 w-16 text-destructive" />}
            </div>

            <div className="p-6 space-y-4">
              <div className="text-center space-y-1.5">
                <h2 className="text-xl font-bold">
                  {isPending && "Registration Under Review"}
                  {isSuspended && "Merchant Account Suspended"}
                  {isRejected && "Application Rejected"}
                </h2>
                <p className="text-xs text-muted-foreground">
                  Store: <strong className="text-foreground">{storeName}</strong>
                </p>
              </div>

              <div className="text-sm text-muted-foreground leading-relaxed text-center">
                {isPending && "Your store registration request is currently pending admin review. We are validating your store location and details. Once approved, you will have full access to the merchant panel."}
                {isSuspended && `Your store has been suspended by the administrator. Reason: ${store.rejectionReason || "Violation of merchant guidelines"}.`}
                {isRejected && `Your store registration application was rejected. Reason: ${store.rejectionReason || "Incomplete store or document details"}.`}
              </div>

              {(isSuspended || isRejected) && (
                <p className="text-xs text-center text-muted-foreground pt-2">
                  Please contact support at <a href="mailto:support@biharimall.com" className="font-semibold text-primary underline">support@biharimall.com</a> to appeal this decision.
                </p>
              )}
            </div>

            <div className="bg-secondary/40 border-t p-4 flex flex-col gap-2">
              <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 gap-2" asChild>
                <Link to="/store/kyc">
                  <FileText className="h-4 w-4" /> Complete KYC / Upload Documents
                </Link>
              </Button>
              <div className="flex gap-4">
                <Button variant="outline" className="w-full" onClick={() => logout()}>
                  <LogOut className="mr-2 h-4 w-4" /> Sign Out
                </Button>
                <Button variant="outline" className="w-full" asChild>
                  <Link to="/">Bihari Mall Home</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentPageTitle =
    pageTitles[location.pathname] ||
    (location.pathname.startsWith("/store/reports") ? "Reports" :
    (location.pathname.startsWith("/store/supply/place-order") ? "Place Order" :
    location.pathname.match(/^\/store\/products\/\d+$/) ? "Product Details" :
    location.pathname.includes("/store/products/") && location.pathname.endsWith("/edit") ? "Edit Product" : "Store"));

  const panelNavItems = buildStoreNav(pendingOrders);

  const topbarActions = (
    <>
      <Badge variant={isOpen ? "default" : "secondary"} className="hidden sm:flex gap-1 text-xs">
        {isOpen ? <ToggleRight className="h-3 w-3" /> : <ToggleLeft className="h-3 w-3" />}
        {isOpen ? "Open" : "Closed"}
      </Badge>
      <NotificationBell scope="me" viewAllPath="/store/notifications" />
      <Separator orientation="vertical" className="h-7 hidden sm:block mx-0.5" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="glass-chip h-10 gap-2 rounded-xl px-1.5 hover:bg-transparent sm:px-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-primary text-primary-foreground text-xs font-bold ring-2 ring-primary/20">
              {initials}
            </div>
            <div className="hidden lg:flex flex-col items-start leading-tight">
              <span className="text-[13px] font-semibold text-foreground">{ownerLabel}</span>
              <span className="text-[10px] text-muted-foreground">Store Owner</span>
            </div>
            <ChevronDown className="h-3 w-3 text-muted-foreground hidden sm:block" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={8} collisionPadding={8} className="w-[min(220px,calc(100vw-1rem))] bg-popover border border-border shadow-2xl z-50 rounded-xl p-1.5">
          <div className="px-2.5 py-3 mb-1 rounded-lg bg-secondary/40">
            <p className="text-sm font-bold text-foreground">{ownerLabel}</p>
            <p className="text-[11px] text-muted-foreground">{ownerEmail}</p>
          </div>
          <DropdownMenuItem className="cursor-pointer gap-2.5 rounded-lg text-sm py-2" asChild>
            <Link to="/store/profile">
              <User className="h-4 w-4 text-muted-foreground" /> Store Profile
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem className="cursor-pointer gap-2.5 rounded-lg text-sm py-2" asChild>
            <Link to="/store/kyc">
              <FileText className="h-4 w-4 text-muted-foreground" /> Store KYC
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem className="cursor-pointer gap-2.5 rounded-lg text-sm py-2" asChild>
            <Link to="/store/earnings">
              <Settings className="h-4 w-4 text-muted-foreground" /> Earnings
            </Link>
          </DropdownMenuItem>
              <DropdownMenuSeparator className="my-1" />
              <DropdownMenuItem
                className="cursor-pointer gap-2.5 rounded-lg text-sm py-2 text-destructive focus:text-destructive focus:bg-destructive/10"
                onClick={() => logout()}
              >
                <LogOut className="h-4 w-4" /> Sign Out
              </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );

  return (
    <ChannelDeskProvider desk={retailerDesk}>
    <PanelLayout
      brand={{
        icon: Store,
        title: isLoadingStore ? "Store Panel" : storeName,
        subtitle: "Store Panel",
        url: "/store",
      }}
      navItems={panelNavItems}
      footer={{ label: "Sign Out", url: "/" }}
      breadcrumbPrefix="Store"
      pageTitle={currentPageTitle}
      sectionLabel="MENU"
      topbarActions={topbarActions}
    />
    </ChannelDeskProvider>
  );
};

export default StoreLayout;
